import { useState } from 'react';
import { Button } from '@material-tailwind/react';
import { ReactComponent as SuccessIcon } from '@assets/icons/Success_light.svg';
import { SET_INSPECTION_DOCUMENT, post } from '@services';
import { LenderDocumentTypeEnum } from '@types';
import { formatWithCommas } from '@utils/formatters';

interface SalePriceProps {
  inspectionId: string;
  primaryColor: string;
  initialValue?: number;
  isContinuing?: boolean;
  onContinue: () => void;
  onSuccess: (value: number) => void;
}

const SalePrice = ({
  inspectionId,
  primaryColor,
  initialValue,
  isContinuing = false,
  onContinue,
  onSuccess
}: SalePriceProps) => {
  const [purchasePrice, setPurchasePrice] = useState(
    initialValue != null ? initialValue.toFixed(2) : ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (value: string) => {
    const sanitized = value.replace(/[^\d.]/g, '');
    const [whole = '', ...decimalParts] = sanitized.split('.');
    const decimal = decimalParts.join('').slice(0, 2);
    setPurchasePrice(decimalParts.length > 0 ? `${whole}.${decimal}` : whole);
    setError('');
  };

  const handleConfirm = async () => {
    const amount = Number(purchasePrice.replace(/,/g, ''));
    if (!purchasePrice || !Number.isFinite(amount) || amount <= 0) {
      setError('Please enter a valid sale price.');
      return;
    }

    setPurchasePrice(amount.toFixed(2));
    setIsSubmitting(true);
    try {
      await post(
        SET_INSPECTION_DOCUMENT,
        {
          inspectionId,
          documentType: LenderDocumentTypeEnum.SALE_DETAILS,
          isNotProvided: true,
          extraFields: {
            saleDetails: {
              vehicleSalePrice: amount
            }
          }
        },
        false
      );
      onSuccess(amount);
      setIsComplete(true);
    } catch {
      setError('Unable to submit the sale price. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <div className="flex min-h-[calc(100vh-140px)] w-full flex-col">
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <SuccessIcon className="mb-8 h-20 w-20" />
          <h2 className="mb-5 text-2xl font-semibold text-grey-900">Success</h2>
          <p className="text-base text-grey-600">
            The sale price has been successfully submitted.
          </p>
        </div>
        <Button
          className="mb-4 w-full rounded-md py-3 text-base normal-case text-white"
          style={{ backgroundColor: primaryColor }}
          disabled={isContinuing}
          onClick={onContinue}>
          {isContinuing ? 'Continuing...' : 'Continue'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-140px)] w-full flex-col">
      <div className="flex-1 pt-10">
        <label
          htmlFor="purchase-price"
          className="mb-5 block text-base font-medium text-grey-700">
          Please enter the agreed sale price
        </label>
        <div className="flex items-center border-b border-grey-300 pb-3">
          <span className="mr-3 text-lg text-grey-700">$</span>
          <input
            id="purchase-price"
            type="text"
            inputMode="decimal"
            value={formatWithCommas(purchasePrice)}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^\d.]/g, '');
              const [whole = '', ...rest] = sanitized.split('.');
              const decimal = rest.join('').slice(0, 2);
              const raw = rest.length > 0 ? `${whole}.${decimal}` : whole;
              setPurchasePrice(formatWithCommas(raw)); // e.g. 35000 -> 35,000
            }}
            onBlur={() => {
              if (purchasePrice) {
                setPurchasePrice(formatWithCommas(purchasePrice)); // -> 35,000.00
              }
            }}
            aria-describedby={error ? 'purchase-price-error' : undefined}
            className="w-full bg-transparent text-lg text-grey-900 outline-none"
          />
        </div>
        {error && (
          <p id="purchase-price-error" className="mt-2 text-sm text-error-500">
            {error}
          </p>
        )}
      </div>
      <Button
        className="mb-4 w-full rounded-md py-3 text-base normal-case text-white"
        style={{ backgroundColor: primaryColor }}
        disabled={isSubmitting}
        onClick={handleConfirm}>
        {isSubmitting ? 'Submitting...' : 'Confirm'}
      </Button>
    </div>
  );
};

export default SalePrice;
